import { skipToken } from '@apollo/client/react';
import { useCallback, useMemo, useState } from 'react';
import { newMessageNotificationEvent } from '@guuru/chat-events-web';
import { createMessageForPresentation } from '@guuru/chat-web';
import { useLiveQuery } from '@guuru/react-hooks';
import GET_CHAT_MESSAGES from '../queries/GetChatMessages';
import NEW_CHAT_MESSAGE from '../subscriptions/NewChatMessage';

const addMessageToCache = (previous, messages) => ({
  ...previous,
  chat: {
    ...previous.chat,
    messages: {
      ...previous.chat?.messages,
      edges: messages,
    },
  },
});

const uniqById = (messages) => {
  const ids = new Set();

  return messages.filter((message) => {
    const id = message?.node?.id;
    if (!id) return true;
    if (ids.has(id)) return false;

    ids.add(id);
    return true;
  });
};

export default ({ chatId, isUser, localMessages = [] }) => {
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);

  const {
    data: {
      chat: {
        status,
        partner = {},
        expert,
        messages: { edges: remoteMessages = [] } = {},
        acceptedAt,
        rating,
      } = {},
    } = {},
    updateQuery,
  } = useLiveQuery(
    GET_CHAT_MESSAGES,
    NEW_CHAT_MESSAGE,
    !chatId
      ? skipToken
      : {
          variables: {
            id: chatId,
          },
          queryOptions: {
            variables: {
              isVisibleByExpert: !isUser,
              isVisibleByUser: isUser,
            },
          },
          subscriptionOptions: {
            variables: {
              forExpert: !isUser,
              forUser: isUser,
            },
            onData: ({ data } = {}) => {
              const newChatMessage = data?.data?.newChatMessage;
              if (!newChatMessage) return;

              let didAppendMessage = false;

              updateQuery((previous) => {
                const messages = previous.chat?.messages?.edges ?? [];

                if (
                  messages.some(
                    (message) => message?.node?.id === newChatMessage.id,
                  )
                ) {
                  return previous;
                }

                didAppendMessage = true;

                return addMessageToCache(previous, [
                  ...messages,
                  {
                    __typename: 'MessageEdge',
                    node: newChatMessage,
                  },
                ]);
              });

              if (!didAppendMessage) return;

              // If the incoming message was sent by the current chat participant
              // it does not count as an unread message.
              //
              // eslint-disable-next-line no-underscore-dangle
              const authorType = newChatMessage.author.__typename;
              const isFromMe = authorType === 'UserAuthor' ? isUser : !isUser;

              // eslint-disable-next-line no-use-before-define
              if (isFromMe || !isUnreadMessagesEnabled) return;

              setUnreadMessagesCount((prevCount) => {
                const nextCount = prevCount + 1;
                if (isUser) newMessageNotificationEvent(nextCount);
                return nextCount;
              });
            },
          },
        },
  );

  const isUnreadMessagesEnabled =
    !isUser
    || (partner?.settings?.appearance?.isUnreadMessagesEnabled ?? false);

  const markMessagesRead = useCallback(() => setUnreadMessagesCount(0), []);

  const messages = useMemo(() => {
    const uniqRemoteMessages = uniqById(remoteMessages);
    const presentationMessages = localMessages.map(
      // eslint-disable-next-line no-underscore-dangle
      (m) => (m.__typename ? m : createMessageForPresentation(m)),
    );
    return chatId && uniqRemoteMessages.length >= presentationMessages.length
      ? uniqRemoteMessages
      : presentationMessages;
  }, [chatId, remoteMessages, localMessages]);

  return {
    status,
    partner,
    expert,
    messages,
    acceptedAt,
    rating,
    unreadMessagesCount,
    incrementUnreadMessages: () => setUnreadMessagesCount((prev) => prev + 1),
    markMessagesRead,
  };
};
